import ArrowLink from "common/ArrowLink/ArrowLink";
import InfoPopup from "common/InfoPopup/InfoPopup";
import Row from "common/Row/Row";
import { useContext } from "react";
import { APIEndpoints } from "utils/APIEndpoints";
import labels from "utils/LocalizedStrings";
import sanitize from "utils/SanitizeHtml";
import {
  amountFormatter,
  getAge,
  getCityStateZip,
  getEmploymentStatus,
  getGender,
  getMaritalStatus,
} from "utils/Utilities";
import { DashboardContext } from "./Dashboard";

const SectionFour = () => {
  const { participantDetails, investmentDetails } =
    useContext(DashboardContext);
  const participantAge = getAge(participantDetails.dob);

  return (
    <section className="section-four px-0">
      {/* As of Date & Glossary of Terms  */}
      <section className="d-flex justify-content-between align-items-end mb-3 flex-wrap">
        <div className="small">
          {labels?.dashboard?.asOf} {investmentDetails?.asOfBalanceDate}
        </div>
        <ArrowLink
          wrapperClassName="glossary-terms"
          path="/glossary-terms"
          text={labels?.dashboard?.glossaryTerms}
          className="justify-content-center"
          iconClassName="d-none"
        />
      </section>

      {/* Your Information  */}
      <section className="mb-3 owner-info">
        <div className="flex-between-end">
          <div className="title h4 col-4 col-sm-6">
            {labels?.dashboard?.balanceInfoTitle}
          </div>

          <div className="d-flex flex-column align-items-end">
            <div className="title fw-bold h5">
              {labels?.dashboard?.makeYourElection}
            </div>
            {participantDetails.phaseCode === "A" && (
              <ArrowLink
                path="/income-election/1"
                text={labels?.dashboard?.incomeElection}
                disabled={
                  participantAge < 65 ||
                  (participantAge == 65 &&
                    (!investmentDetails?.stepUpCompleted ||
                      !investmentDetails?.accountBalance)) ||
                  (participantAge > 65 && !investmentDetails?.accountBalance)
                }
              />
            )}
            {participantDetails.phaseCode === "W" &&
              investmentDetails?.monthlyPayoutsInfo?.payoutStatusCode ===
                "A" && (
                <ArrowLink
                  path="/suspend-monthly-payout"
                  text={labels?.dashboard?.suspendMonthlyPayout}
                />
              )}
            {participantDetails.phaseCode === "W" &&
              (!investmentDetails?.monthlyPayoutsInfo?.payoutStartDate ||
                investmentDetails?.monthlyPayoutsInfo?.payoutStatusCode ===
                  "S") && (
                <ArrowLink
                  path="/monthly-payout-election/1"
                  text={labels?.dashboard?.monthlyPayoutElection}
                />
              )}
          </div>
        </div>
      </section>

      {/* Account Balance  */}
      <section className="mb-4">
        <div className="d-flex flex-wrap-mobile justify-content-between">
          <div className="col-12 col-md-8 mb-3">
            <div className="fw-bold  border-bottom-1 pb-2 mb-2">
              {labels?.dashboard?.accountMarketValue}
              <InfoPopup
                position={"top"}
                content={labels?.dashboard?.actMarketValueDescription}
              />
            </div>
            {investmentDetails?.fundLevelBalance?.map((e) => {
              return (
                e.marketValue > 0 && (
                  <Row
                    key={e.fundName}
                    leftValue={e.fundName}
                    rightValue={amountFormatter(e.marketValue)}
                    noBorder={true}
                  />
                )
              );
            })}
          </div>
          <div className="mx-2 separator" />
          <div className="col-12 col-md-3">
            <div className="fw-bold border-bottom-1 pb-2 mb-2">
              {labels?.dashboard?.benefitBase}
              <InfoPopup
                position={"top"}
                content={labels?.dashboard?.benefitBaseDescription}
              />
            </div>
            {amountFormatter(investmentDetails?.incomeBase)}
          </div>
        </div>
      </section>

      {participantDetails?.phaseCode === "W" ? (
        <>
          {/* Income Benefit Election  */}
          <section className="mb-5">
            <div className="flex-between-end title h4">
              {labels?.dashboard?.incomeBenefitElection?.title}
            </div>
            <p>{labels?.dashboard?.incomeBenefitElection?.description}</p>
            <div>
              {labels?.dashboard?.incomeBenefitElection?.electionDate}{" "}
              {investmentDetails?.electionInfo?.electionDate}
            </div>
            <div>
              {labels?.dashboard?.incomeBenefitElection?.electionType}{" "}
              {investmentDetails?.electionInfo?.electionType}
            </div>
            {investmentDetails?.electionInfo?.jointAnnuitantInfo?.ssn && (
              <ArrowLink
                wrapperClassName="joint-life"
                path="/joint-life-information"
                pathState={investmentDetails?.electionInfo?.jointAnnuitantInfo}
                text={labels?.dashboard?.incomeBenefitElection?.jointLifeInfo}
                iconClassName="d-none"
              />
            )}
          </section>

          {/* Monthly Installment Payouts */}
          {investmentDetails?.monthlyPayoutsInfo?.payoutStatusCode === "A" && (
            <section className="mb-5">
              <div className="flex-between-end flex-wrap">
                <div className="title h4">
                  {labels?.dashboard?.monthlyPayouts?.title}
                </div>
                <span className="small">
                  {labels?.dashboard?.asOf} {investmentDetails?.asOfBalanceDate}
                </span>
              </div>

              <>
                <div className="fw-bold mb-1">
                  {labels?.dashboard?.monthlyPayouts?.yourCurrentPayoutInfo}
                </div>
                <Row
                  leftValue={
                    labels?.dashboard?.monthlyPayouts?.currentWdlPeriod
                  }
                  rightValue={
                    investmentDetails?.monthlyPayoutsInfo
                      ?.currentWdlPeriodStartDate +
                    " - " +
                    investmentDetails?.monthlyPayoutsInfo
                      ?.currentWdlPeriodEndDate
                  }
                  noBorder={true}
                  showInfoPopup={true}
                  infoPopupMessage={
                    labels?.dashboard?.monthlyPayouts?.currentWdlPeriodInfoMsg
                  }
                />
                <Row
                  leftValue={labels?.dashboard?.monthlyPayouts?.startDate}
                  rightValue={
                    investmentDetails?.monthlyPayoutsInfo?.payoutStartDate
                  }
                  noBorder={true}
                />
                <Row
                  leftValue={
                    labels?.dashboard?.monthlyPayouts
                      ?.estimatedMonthlyInstallmentPayoutAmount
                  }
                  rightValue={amountFormatter(
                    investmentDetails?.monthlyPayoutsInfo
                      ?.estimatedMonthlyPayout
                  )}
                  noBorder={true}
                  rightClass="fw-bold"
                />
                <Row
                  leftValue={labels?.dashboard?.monthlyPayouts?.nextPayOutDate}
                  rightValue={
                    investmentDetails?.monthlyPayoutsInfo?.nextPayoutDate ||
                    "N/A"
                  }
                  noBorder={true}
                />
                <Row
                  leftValue={
                    labels?.dashboard?.monthlyPayouts?.nextPayoutAmount
                  }
                  rightValue={amountFormatter(
                    investmentDetails?.monthlyPayoutsInfo?.nextPayoutAmount
                  )}
                  noBorder={true}
                />
                <Row
                  leftValue={labels?.dashboard?.monthlyPayouts?.lastPayoutDate}
                  rightValue={
                    investmentDetails?.monthlyPayoutsInfo?.lastPayoutDate ||
                    "N/A"
                  }
                  noBorder={true}
                />
                <Row
                  leftValue={
                    labels?.dashboard?.monthlyPayouts?.lastPayoutAmount
                  }
                  rightValue={amountFormatter(
                    investmentDetails?.monthlyPayoutsInfo?.lastPayoutAmount
                  )}
                  noBorder={true}
                />
                <Row
                  leftValue={
                    labels?.dashboard?.monthlyPayouts?.amountWithdrawnToDate
                  }
                  rightValue={amountFormatter(
                    investmentDetails?.monthlyPayoutsInfo?.amountWithdrawnToDate
                  )}
                  noBorder={true}
                />
                <Row
                  leftValue={
                    labels?.dashboard?.monthlyPayouts?.remainingWithdrawalAmount
                  }
                  rightValue={amountFormatter(
                    investmentDetails?.monthlyPayoutsInfo?.remainingWdlAmount
                  )}
                  noBorder={true}
                />
                <Row
                  leftValue={
                    labels?.dashboard?.monthlyPayouts?.excessWithdrawalAmount
                  }
                  rightValue={amountFormatter(
                    investmentDetails?.monthlyPayoutsInfo?.excessWdlAmount
                  )}
                  noBorder={true}
                  wrapperClassNames="mb-3"
                />

                <div className="fw-bold mb-1">
                  {labels?.dashboard?.monthlyPayouts?.yourNextPayoutWindow}
                </div>
                <Row
                  leftValue={
                    labels?.dashboard?.monthlyPayouts
                      ?.monthlyInstallmentPayoutResetDate
                  }
                  rightValue={
                    investmentDetails?.monthlyPayoutsInfo
                      ?.monthlyPayoutResetDate
                  }
                  noBorder={true}
                  showInfoPopup={true}
                  infoPopupMessage={
                    labels?.dashboard?.monthlyPayouts
                      ?.monthlyInstallmentPayoutResetDateInfoMsg
                  }
                />
                <Row
                  leftValue={
                    labels?.dashboard?.monthlyPayouts?.nextWithdrawalPeriod
                  }
                  rightValue={
                    investmentDetails?.monthlyPayoutsInfo
                      ?.nextWdlPeriodStartDate +
                    " - " +
                    investmentDetails?.monthlyPayoutsInfo?.nextWdlPeriodEndDate
                  }
                  noBorder={true}
                  showInfoPopup={true}
                  infoPopupMessage={
                    labels?.dashboard?.monthlyPayouts
                      ?.nextWithdrawalPeriodInfoMsg
                  }
                  wrapperClassNames="mb-4"
                />

                <span
                  dangerouslySetInnerHTML={sanitize(
                    labels?.dashboard?.monthlyPayouts?.description
                  )}
                ></span>
              </>
            </section>
          )}
        </>
      ) : (
        <>
          {/* Projected Monthly Payout */}
          <section className="mb-5">
            <div className="flex-between-end title h4">
              {labels?.dashboard?.projectedMonthlyPayout?.title}
            </div>

            <div className="d-flex justify-content-between">
              <div className="col-md-9">
                <span
                  dangerouslySetInnerHTML={sanitize(
                    labels?.dashboard?.projectedMonthlyPayout?.description
                  )}
                />
                <div className="col-md-11 mt-3 flex-between-center">
                  <div className="fw-bold">
                    {
                      labels?.dashboard?.projectedMonthlyPayout
                        ?.singleLifeOption
                    }
                    <InfoPopup
                      position={"top"}
                      content={
                        labels?.dashboard?.projectedMonthlyPayout
                          ?.singleLifeOptionDescription
                      }
                    />
                  </div>
                  <div className="text-end">
                    <div
                      className="title h5"
                      dangerouslySetInnerHTML={sanitize(
                        labels?.dashboard?.projectedMonthlyPayout?.age65.replace(
                          "~electionAge~",
                          participantAge <= 65 ? "65" : participantAge
                        )
                      )}
                    ></div>
                    <div className="title h2">
                      {amountFormatter(
                        investmentDetails?.projectedMonthlyPayout
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <img
                  className="bottomImage"
                  src={APIEndpoints.images(
                    localStorage.getItem("tenant"),
                    "payoutImage.jpg"
                  )}
                  alt="payoutImage"
                  width="100%"
                  height="100%"
                />
              </div>
            </div>
          </section>
        </>
      )}

      {participantDetails.phaseCode !== "A" && (
        <section className="mb-5 owner-info">
          <div className="flex-between-end">
            <div className="title h4 col-4 col-sm-6">
              {labels?.dashboard?.ownerInformation}
            </div>
          </div>
          <>
            <p
              dangerouslySetInnerHTML={sanitize(
                labels?.dashboard?.ownerInfoDescription
              )}
            ></p>
            <div className="d-flex flex-wrap-mobile px-2">
              <div className="col-12 col-md-6 pe-5">
                <Row
                  leftValue={labels?.dashboard?.name}
                  rightValue={`${participantDetails?.firstName} ${participantDetails?.middleName} ${participantDetails.lastName}`}
                  noBorder={true}
                />
                <Row
                  leftValue={labels?.dashboard?.address}
                  rightValue={participantDetails?.address1}
                  noBorder={true}
                />
                <Row
                  leftValue={labels?.dashboard?.cityStateZip}
                  rightValue={getCityStateZip(participantDetails)}
                  noBorder={true}
                />
                <Row
                  leftValue={labels?.dashboard?.country}
                  rightValue={
                    participantDetails?.usResident === "Y"
                      ? labels?.dashboard?.unitedStates
                      : participantDetails?.pptStateCountryValue
                  }
                  noBorder={true}
                />
              </div>
              <div className="border-div"></div>
              <div className="col-12 col-md-6 ps-5">
                <Row
                  leftValue={labels?.dashboard?.dateOfBirth}
                  rightValue={participantDetails.dob}
                  noBorder={true}
                />
                <Row
                  leftValue={labels?.dashboard?.gender}
                  rightValue={getGender(participantDetails?.genderCode)}
                  noBorder={true}
                />
                <Row
                  leftValue={labels?.dashboard?.maritalStatus}
                  rightValue={getMaritalStatus(
                    participantDetails?.maritalStatusCode
                  )}
                  noBorder={true}
                />
                <Row
                  leftValue={labels?.dashboard?.employmentStatus}
                  rightValue={getEmploymentStatus(
                    participantDetails?.employmentStatusCode
                  )}
                  noBorder={true}
                />
              </div>
            </div>
          </>
        </section>
      )}
    </section>
  );
};

export default SectionFour;
